import { useState, useRef, useEffect } from 'react';
import { Form, ListGroup } from 'react-bootstrap';
import { FaSearch, FaTimes } from 'react-icons/fa';
import '../../assets/styles/searchableSelect.css';

const SearchableSelect = ({
    options = [],
    value,
    onChange,
    placeholder = 'Seleccione una opción',
    searchPlaceholder = 'Buscar...',
    disabled = false,
    required = false,
    label,
    name,
    getOptionLabel = (option) => option,
    getOptionValue = (option) => option,
    isOptionDisabled = () => false,
    renderOption = null,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        if (value) {
            const selectedOption = options.find(opt => getOptionValue(opt) === value);
            if (selectedOption) {
                setSelectedLabel(getOptionLabel(selectedOption));
            }
        } else {
            setSelectedLabel('');
        }
    }, [value, options, getOptionLabel, getOptionValue]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowDropdown(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (showDropdown && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [showDropdown]);

    const filteredOptions = options.filter(option => {
        const label = getOptionLabel(option).toLowerCase();
        return label.includes(searchTerm.toLowerCase());
    });

    const handleSelectOption = (option) => {
        const optionValue = getOptionValue(option);
        // Create a proper event-like object that mimics a DOM event
        const syntheticEvent = {
            target: {
                name: name,
                value: optionValue,
                getAttribute: (attr) => {
                    if (attr === 'type') return null;
                    return null;
                }
            }
        };
        onChange(syntheticEvent);
        setShowDropdown(false);
        setSearchTerm('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        // Create a proper event-like object for clearing
        const syntheticEvent = {
            target: {
                name: name,
                value: '',
                getAttribute: (attr) => {
                    if (attr === 'type') return null;
                    return null;
                }
            }
        };
        onChange(syntheticEvent);
        setSearchTerm('');
    };

    const handleToggleDropdown = () => {
        if (!disabled) {
            setShowDropdown(!showDropdown);
            if (!showDropdown) {
                setSearchTerm('');
            }
        }
    };

    return (
        <div className="searchable-select-wrapper" ref={containerRef}>
            {label && (
                <Form.Label column='sm' className="small fw-bold">
                    {label}
                </Form.Label>
            )}

            <div
                className={`searchable-select-control ${disabled ? 'disabled' : ''} ${showDropdown ? 'active' : ''}`}
                onClick={handleToggleDropdown}
            >
                <span className={`selected-value ${!selectedLabel ? 'placeholder' : ''}`}>
                    {selectedLabel || placeholder}
                </span>
                {value && !disabled && (
                    <FaTimes
                        className="clear-icon"
                        onClick={handleClear}
                        size={14}
                    />
                )}
            </div>

            {showDropdown && (
                <>
                    <div className="searchable-select-backdrop" onClick={() => setShowDropdown(false)} />
                    <div className="searchable-select-dropdown shadow-lg">
                        <div className="search-input-wrapper">
                            <FaSearch className="search-icon" size={14} />
                            <Form.Control
                                ref={searchInputRef}
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                                onClick={(e) => e.stopPropagation()}
                            />
                            {searchTerm && (
                                <FaTimes
                                    className="clear-search-icon"
                                    onClick={() => setSearchTerm('')}
                                    size={14}
                                />
                            )}
                        </div>

                        <ListGroup className="options-list" variant="flush">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option, idx) => {
                                    const optionValue = getOptionValue(option);
                                    const optionLabel = getOptionLabel(option);
                                    const disabled = isOptionDisabled(option);

                                    return (
                                        <ListGroup.Item
                                            key={idx}
                                            className={`option-item ${value === optionValue ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                                            onClick={() => !disabled && handleSelectOption(option)}
                                            disabled={disabled}
                                        >
                                            {renderOption ? renderOption(option) : optionLabel}
                                        </ListGroup.Item>
                                    );
                                })
                            ) : (
                                <ListGroup.Item className="option-item no-results">
                                    No se encontraron resultados
                                </ListGroup.Item>
                            )}
                        </ListGroup>
                    </div>
                </>
            )}

            {/* Hidden select for form validation */}
            <Form.Select
                name={name}
                value={value}
                onChange={() => {}}
                required={required}
                style={{ display: 'none' }}
                tabIndex={-1}
            />
        </div>
    );
};

export default SearchableSelect;



